<template>
  <header class="site-header">
    <a
      v-if="config.announcement && config.announcement.visible"
      :href="config.announcement.href || '#'"
      class="site-announcement"
    >
      {{ config.announcement.text }}
    </a>

    <nav class="container site-nav" aria-label="منوی اصلی">
      <div class="site-nav__bar">
        <component
          :is="logoLinkComponent"
          v-bind="logoLinkProps"
          class="site-logo"
          aria-label="صفحه اصلی"
        >
          <img v-if="logo.src" :src="logo.src" :alt="logo.alt || ''" />
          <span v-else>{{ globalContent.companyName || 'همراه زر' }}</span>
        </component>

        <ul class="site-menu d-none d-lg-flex">
          <li
            v-for="item in visibleMenu"
            :key="item._id || item.href"
            class="site-menu__item"
          >
            <component
              :is="linkComponent(item.href)"
              v-bind="linkProps(item.href)"
              class="site-menu__link"
              :class="{ 'site-menu__link--active': isActivePath(item.href) }"
            >
              <span>{{ item.label }}</span>
              <span v-if="item.badge" class="site-menu__badge">{{ item.badge }}</span>
            </component>

            <ul
              v-if="item.children && item.children.length"
              class="site-submenu"
            >
              <li
                v-for="child in item.children.filter((entry) => entry.visible !== false)"
                :key="child._id || child.href"
              >
                <component
                  :is="linkComponent(child.href)"
                  v-bind="linkProps(child.href)"
                  class="site-submenu__link"
                >
                  {{ child.label }}
                </component>
              </li>
            </ul>
          </li>
        </ul>

        <div class="site-actions d-none d-lg-flex">
          <router-link
            v-if="isAuthenticated"
            class="site-btn site-btn--ghost"
            :to="dashboardRoute"
          >
            {{ actions.dashboardLabel || 'داشبورد' }}
          </router-link>
          <router-link
            v-else-if="actions.showLogin !== false"
            class="site-btn site-btn--ghost"
            :to="loginRoute"
          >
            {{ actions.loginLabel || 'ورود' }}
          </router-link>

          <router-link
            class="site-btn site-btn--primary"
            :to="profileOrRegisterRoute"
          >
            {{ profileOrRegisterLabel }}
          </router-link>

          <router-link
            v-if="isAuthenticated"
            class="site-btn site-btn--text"
            :to="logoutRoute"
          >
            {{ actions.logoutLabel || 'خروج' }}
          </router-link>
        </div>

        <button
          type="button"
          class="site-mobile-toggle d-lg-none"
          :aria-expanded="isMobileMenuOpen.toString()"
          aria-label="باز کردن منو"
          @click="toggleMobileMenu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <transition name="mobile-menu">
        <div v-if="isMobileMenuOpen" class="site-mobile-panel d-lg-none">
          <ul class="site-mobile-menu">
            <li
              v-for="item in visibleMobileMenu"
              :key="item._id || item.href"
              class="site-mobile-menu__item"
            >
              <component
                :is="linkComponent(item.href)"
                v-bind="linkProps(item.href)"
                class="site-mobile-menu__link"
                @click="closeMobileMenu"
              >
                {{ item.label }}
              </component>

              <ul
                v-if="item.children && item.children.length"
                class="site-mobile-submenu"
              >
                <li
                  v-for="child in item.children.filter((entry) => entry.visible !== false)"
                  :key="child._id || child.href"
                >
                  <component
                    :is="linkComponent(child.href)"
                    v-bind="linkProps(child.href)"
                    class="site-mobile-submenu__link"
                    @click="closeMobileMenu"
                  >
                    {{ child.label }}
                  </component>
                </li>
              </ul>
            </li>
          </ul>

          <div class="site-mobile-actions">
            <router-link
              v-if="isAuthenticated"
              class="site-btn site-btn--ghost"
              :to="dashboardRoute"
              @click="closeMobileMenu"
            >
              {{ actions.dashboardLabel || 'داشبورد' }}
            </router-link>
            <router-link
              v-else-if="actions.showLogin !== false"
              class="site-btn site-btn--ghost"
              :to="loginRoute"
              @click="closeMobileMenu"
            >
              {{ actions.loginLabel || 'ورود' }}
            </router-link>

            <router-link
              class="site-btn site-btn--primary"
              :to="profileOrRegisterRoute"
              @click="closeMobileMenu"
            >
              {{ profileOrRegisterLabel }}
            </router-link>

            <router-link
              v-if="isAuthenticated"
              class="site-btn site-btn--text"
              :to="logoutRoute"
              @click="closeMobileMenu"
            >
              {{ actions.logoutLabel || 'خروج' }}
            </router-link>
          </div>
        </div>
      </transition>
    </nav>
  </header>
</template>

<script>
import {seoBrand} from "@/services/seoBrand";
import { englishToPersianDigits } from '@/utils/numberFormatter';
import { getSiteCms, loadSiteCms } from '@/services/siteCms';

export default {
  emits: ['toggle-blur'],
  data() {
    return {
      cms: getSiteCms(),
      isMobileMenuOpen: false,
    };
  },
  computed: {
    config() { return this.cms.header || {}; },
    logo() { return seoBrand.logo ? {...(this.config.logo||{}),src:seoBrand.logo,alt:seoBrand.siteName} : this.config.logo || {}; },
    actions() { return this.config.actions || {}; },
    globalContent() { return this.cms.globalContent || {}; },
    visibleMenu() {
      return (this.config.desktopMenu || []).filter((item) => item.visible !== false);
    },
    visibleMobileMenu() {
      const source = this.config.mobileMenu?.length
        ? this.config.mobileMenu
        : this.config.desktopMenu || [];
      return source.filter((item) => item.visible !== false);
    },
    isAuthenticated() {
      return Boolean(this.$store?.state?.userIsAuthenticated || this.$store?.state?.userToken);
    },
    logoLinkComponent() { return this.linkComponent(this.logo.href); },
    logoLinkProps() { return this.linkProps(this.logo.href); },
    dashboardRoute() { return { path: '/user/dashboard' }; },
    loginRoute() { return { path: '/user/auth/login' }; },
    registerRoute() { return { path: '/user/auth/login', query: { mobile: '' } }; },
    logoutRoute() { return { path: '/user/logout' }; },
    userFullName() {
      const user = this.storeUser;
      const first = (user.firstName || user.firstname || user.first_name || '').toString().trim();
      const last = (user.lastName || user.lastname || user.last_name || '').toString().trim();
      return [first, last].filter(Boolean).join(' ');
    },
    userMobileFa() {
      const user = this.storeUser;
      const mobile = (user.mobile || user.phone || user.phoneNumber || '').toString().trim();
      return mobile ? englishToPersianDigits(mobile) : '';
    },
    storeUser() {
      const userDetails = this.$store?.state?.userDetails || {};
      let fallbackUser = {};
      try {
        fallbackUser = JSON.parse(localStorage.getItem('hz_user_cache') || '{}');
      } catch (_) {}
      return { ...fallbackUser, ...userDetails };
    },
    profileOrRegisterLabel() {
      if (!this.isAuthenticated) return this.actions.registerLabel || 'ثبت نام';
      return this.userFullName || this.userMobileFa || (this.actions.dashboardLabel || 'داشبورد');
    },
    profileOrRegisterRoute() {
      return this.isAuthenticated ? this.dashboardRoute : this.registerRoute;
    },
  },
  watch: {
    '$route.fullPath'() {
      this.closeMobileMenu();
    },
  },
  async created() {
    this.cms = await loadSiteCms();
    window.addEventListener('site-cms:loaded', this.updateCms);
  },
  unmounted() {
    window.removeEventListener('site-cms:loaded', this.updateCms);
  },
  methods: {
    updateCms(event) { this.cms = event.detail || {}; },
    toggleMobileMenu() { this.isMobileMenuOpen = !this.isMobileMenuOpen; },
    closeMobileMenu() { this.isMobileMenuOpen = false; },
    isInternalLink(href) {
      return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
    },
    isActivePath(href) {
      return this.isInternalLink(href) && this.$route?.path === href;
    },
    linkComponent(href) {
      return this.isInternalLink(href) ? 'router-link' : 'a';
    },
    linkProps(href) {
      return this.isInternalLink(href)
        ? { to: href || '/' }
        : { href: href || '#', target: '_self' };
    },
  },
};
</script>

<style scoped>
.site-header {
  position: relative;
  z-index: 20;
  background:
    linear-gradient(135deg, rgba(19, 20, 25, 0.98), rgba(11, 14, 25, 0.96));
  border-bottom: 1px solid rgba(212, 175, 55, 0.12);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
}

.site-announcement {
  display: block;
  padding: 0.6rem 1rem;
  color: #101010;
  text-align: center;
  text-decoration: none;
  background: linear-gradient(90deg, #d4af37, #f0d77a);
  font-weight: 700;
}

.site-nav {
  direction: rtl;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.site-nav__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.site-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  min-height: 56px;
  color: #fff;
  text-decoration: none;
  font-weight: 800;
}

.site-logo img {
  max-height: 52px;
  max-width: 170px;
  object-fit: contain;
}

.site-menu {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.site-menu__item {
  position: relative;
}

.site-menu__link,
.site-submenu__link,
.site-mobile-menu__link,
.site-mobile-submenu__link {
  text-decoration: none;
}

.site-menu__link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.8rem 1rem;
  color: rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  transition: all 0.2s ease;
  font-weight: 600;
}

.site-menu__link:hover,
.site-menu__link--active {
  color: #fff;
  background: rgba(212, 175, 55, 0.14);
}

.site-menu__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.6rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  color: #131419;
  background: #d4af37;
  font-size: 0.72rem;
}

.site-submenu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 210px;
  margin: 0;
  padding: 0.75rem;
  list-style: none;
  border: 1px solid rgba(212, 175, 55, 0.14);
  border-radius: 20px;
  background: rgba(18, 20, 30, 0.98);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.26);
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px);
  transition: all 0.2s ease;
}

.site-menu__item:hover .site-submenu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.site-submenu__link {
  display: block;
  padding: 0.7rem 0.85rem;
  color: rgba(255, 255, 255, 0.88);
  border-radius: 14px;
}

.site-submenu__link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}

.site-actions {
  align-items: center;
  gap: 0.75rem;
}

.site-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0.72rem 1.15rem;
  border: 1px solid transparent;
  border-radius: 16px;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.2s ease;
}

.site-btn--primary {
  color: #101010;
  background: linear-gradient(135deg, #d4af37, #f0d77a);
  box-shadow: 0 12px 24px rgba(212, 175, 55, 0.2);
}

.site-btn--primary:hover {
  color: #101010;
  transform: translateY(-1px);
}

.site-btn--ghost {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}

.site-btn--ghost:hover {
  color: #fff;
  background: rgba(212, 175, 55, 0.14);
  border-color: rgba(212, 175, 55, 0.24);
}

.site-btn--text {
  color: #d7b14a;
  background: transparent;
}

.site-btn--text:hover {
  color: #f0d77a;
}

.site-mobile-toggle {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.3rem;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
}

.site-mobile-toggle span {
  display: block;
  width: 20px;
  height: 2px;
  margin: 0 auto;
  background: #fff;
  border-radius: 999px;
}

.site-mobile-panel {
  direction: rtl;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(212, 175, 55, 0.12);
  border-radius: 24px;
  background: rgba(15, 17, 26, 0.98);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.22);
}

.site-mobile-menu,
.site-mobile-submenu {
  margin: 0;
  padding: 0;
  list-style: none;
}

.site-mobile-menu__item + .site-mobile-menu__item {
  margin-top: 0.7rem;
}

.site-mobile-menu__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.95rem 1rem;
  color: #fff;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 18px;
  font-weight: 700;
}

.site-mobile-submenu {
  margin-top: 0.55rem;
  padding-right: 0.5rem;
}

.site-mobile-submenu__link {
  display: block;
  padding: 0.7rem 1rem;
  color: rgba(255, 255, 255, 0.82);
  border-radius: 14px;
}

.site-mobile-actions {
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.2s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 991.98px) {
  .site-nav {
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
  }

  .site-logo img {
    max-height: 46px;
  }
}
</style>
